import { Link as GatsbyLink } from "gatsby";
import React from "react";
import styled from "styled-components";

import { fontSizes, isAnathoth, MOBILE_BREAKPOINT } from "@util/constants";
import { A, Container, GiveMeSomeSpace, P } from "@util/standard";
import { Dimensions } from "@util/types";
import Loading from "./loading";

const StyledButton = styled.button<{
  dimensions?: Dimensions;
  mobileDimensions?: Dimensions;
  mobileMargin?: string;
  margin?: string;
  minHeight?: number;
  width?: string;
  padding?: string;
  fontSize?: number;
  disabled?: boolean;
}>`
  font-size: ${({ fontSize }) => {
    if (fontSize) return `${fontSize}px`;
    if (isAnathoth) return `14px`;
    return `16px`;
  }};
  font-weight: 600;
  border-radius: 100px;
  text-align: center;
  cursor: pointer;
  a {
    text-decoration: none;
  }
  user-select: none;
  ${({ margin }) => margin && `margin: ${margin};`}
  padding: ${props => (props.padding ? props.padding : `15px 30px;`)};
  ${({ width }) => width && `width: ${width};`}
  height: ${props => props.dimensions?.height ?? `auto`};
  letter-spacing: 0.08em;

  &.transparent {
    color: var(--theme-button-color);
    border: 1px solid var(--theme-button-color);
    background-color: transparent;

    &:hover {
      color: white;
      border: 1px solid var(--theme-button-color);
      background-color: var(--theme-button-color);
    }
  }
  &.base {
    color: white;
    border: 1px solid var(--theme-button-color);
    background-color: var(--theme-button-color);
    &:hover {
      color: var(--theme-button-color);
      border: 1px solid var(--theme-button-color);
      background-color: transparent;
    }
  }
  &.white {
    color: var(--theme-button-color);
    border: 1px solid white;
    background-color: white;
  }
  &.white-outline {
    color: white;
    border: 1px solid white;
    background-color: transparent;

    &:hover {
      color: var(--theme-button-color);

      background-color: white;
    }
  }

  transition: background-color 0.4s linear;
  min-width: 200px;

  &:focus {
    outline: 0;
  }
  ${({ disabled }) =>
    disabled &&
    `
    opacity: 0.5;
    pointer-events: none;
  `};
  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    ${({ mobileDimensions }) => mobileDimensions && `width:${mobileDimensions}`}
    ${({ mobileMargin }) => mobileMargin && `margin: ${mobileMargin};`}
    line-height: ${fontSizes.h3.mobile * 1.2}px;
    padding: ${props => (props.padding ? props.padding : `5px 30px`)};
    font-size: ${props => props.fontSize ?? "12"}px;
    min-width: 170px;
  }

  ${({ minHeight }) => minHeight && `min-height: ${minHeight}px;`}
`;

const LoadingContainer = styled(Container)`
  flex: 1;
  justify-content: center;
`;

interface Props {
  theme?: string;
  disabled?: boolean;
  className?: string;
  dimensions?: Dimensions;
  mobileDimensions?: Dimensions;
  minHeight?: number;
  text: string;
  margin?: string;
  padding?: string;
  mobileMargin?: string;
  onClick?: (args?: any) => void;
  loading?: boolean;
  type?: "button" | "submit" | "reset";
  linkTo?: string;
  linkToNewWindow?: string;
  width?: string;
}

const Button = ({
  theme = "base",
  disabled,
  dimensions,
  width,
  mobileDimensions,
  text,
  margin,
  onClick,
  mobileMargin,
  loading,
  minHeight,
  padding,
  type,
  linkTo,
  linkToNewWindow,
}: Props) => {
  const Loader = () => (
    <LoadingContainer>
      <>
        <Loading />
        <GiveMeSomeSpace space={3} />
        <P noMargin color="white">
          Loading..
        </P>
      </>
    </LoadingContainer>
  );

  const handleOnClick = () => {
    if (loading) {
      return;
    }
    if (onClick) {
      onClick();
    }
  };

  const RenderedButton = ({
    externalLink,
  }: {
    externalLink?: string | undefined;
  }) => (
    <StyledButton
      dimensions={dimensions}
      width={width}
      disabled={disabled}
      onClick={handleOnClick}
      margin={margin}
      padding={padding}
      mobileDimensions={mobileDimensions}
      mobileMargin={mobileMargin}
      minHeight={minHeight}
      type={type}
      className={theme}
    >
      {loading ? (
        <Loader />
      ) : externalLink ? (
        <A
          href={externalLink}
          hoverColor="white"
          target="_blank"
          rel="noreferrer"
        >
          {text}
        </A>
      ) : (
        text
      )}
    </StyledButton>
  );

  if (linkTo) {
    return (
      <GatsbyLink to={linkTo} >
        <RenderedButton />
      </GatsbyLink>
    );
  }

  return <RenderedButton externalLink={linkToNewWindow} />;
};

export default Button;
